import { uuidDaUrl } from '../../componentes/filtros-lista';

/** CRM no novo desenho: caminhos das páginas do CRM na casca nova (`paginasCasca` do módulo). */
export const CAMINHO_COMERCIAL = '/crm/comercial';
export const CAMINHO_ATIVIDADES = '/crm/atividades';
export const CAMINHO_CONFIGURACOES = '/crm/configuracoes';

/** Ficha da empresa (página Empresas do CRM). */
export const caminhoDaEmpresa = (id: string) => `/crm/empresas/${id}`;

/** Ficha do contato (página Contatos do CRM). */
export const caminhoDoContato = (id: string) => `/crm/contatos/${id}`;

/** rcrm4 SPEC-B2: a ficha do cliente da negociação pelo tipo do parceiro (pessoa vai a Contatos; empresa, a Empresas). */
export const caminhoDoParceiro = (id: string, tipo: string | null | undefined) => (tipo === 'pessoa' ? caminhoDoContato(id) : caminhoDaEmpresa(id));

/** Ficha da negociação em página (lote 4). */
export const caminhoDaNegociacao = (id: string) => `${CAMINHO_COMERCIAL}/negociacao/${id}`;
export const caminhoDaProposta = (id: string) => `${caminhoDaNegociacao(id)}/proposta`;

/** Relatório completo: sub-rota das Atividades (abre por "Ver relatório completo"). */
export const CAMINHO_RELATORIOS = '/crm/atividades/relatorio';

/** Primeiro e último dia do mês de `agora` (datas locais AAAA-MM-DD), o período inicial do relatório. */
export function mesAtual(agora: Date = new Date()): { de: string; ate: string } {
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { de: iso(new Date(agora.getFullYear(), agora.getMonth(), 1)), ate: iso(new Date(agora.getFullYear(), agora.getMonth() + 1, 0)) };
}

/**
 * Lote crm5 (E4): "Ver relatório completo" das Atividades abre o relatório (o "Dashboard" da origem) com o funil, o
 * responsável e o mês atual. Sem funil, o padrão; sem responsável, todas as negociações.
 */
export const caminhoDoRelatorio = (o: { funil?: string; responsavel?: string; agora?: Date } = {}) => {
  const { de, ate } = mesAtual(o.agora);
  return `${CAMINHO_RELATORIOS}?${new URLSearchParams({ ...(o.funil ? { funil: o.funil } : {}), ...(o.responsavel ? { responsavel: o.responsavel } : {}), de, ate })}`;
};

const dataDaUrl = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) ? v : '');

/** Lê o pedido no endereço do relatório (o inverso de `caminhoDoRelatorio`): ids só se uuid, datas só se válidas (senão, o mês atual). */
export function lerPedidoDoRelatorioCrm(busca: string, agora: Date = new Date()): { funil: string; responsavel: string; de: string; ate: string } {
  const q = new URLSearchParams(busca);
  const mes = mesAtual(agora);
  const de = dataDaUrl(q.get('de'));
  const ate = dataDaUrl(q.get('ate'));
  const valido = de && ate && de <= ate;
  return { funil: uuidDaUrl(q.get('funil')), responsavel: uuidDaUrl(q.get('responsavel')), de: valido ? de : mes.de, ate: valido ? ate : mes.ate };
}

/**
 * Lote crm8 (item 6): o tipo de relatório pedido no endereço (`tipo`, um dos da origem). Sem ele, o endereço com período (o
 * "Ver relatório completo" das Atividades) abre o Dashboard, como na origem; o menu (sem nada) abre o Painel.
 */
export function tipoDoRelatorioCrm(busca: string): 'painel' | 'dashboard' | 'pipeline' | 'equipes' | 'feedback' | 'conversoes' | 'produtos' | 'fontes' | 'metas' | 'ligacoes' | 'comparativo' {
  const q = new URLSearchParams(busca);
  const tipos = ['painel', 'dashboard', 'pipeline', 'equipes', 'feedback', 'conversoes', 'produtos', 'fontes', 'metas', 'ligacoes', 'comparativo'] as const;
  const pedido = tipos.find((x) => x === q.get('tipo'));
  return pedido ?? (q.get('de') ? 'dashboard' : 'painel');
}

/** Ajustes do CRM 27/10 (K3): "Ver negociações sem tarefa" do atalho de tarefas abre o Comercial com esse filtro. */
export const CAMINHO_SEM_TAREFA = `${CAMINHO_COMERCIAL}?sem_tarefa=1`;

/**
 * Lê o pedido no endereço do Comercial (o `estatisticas=1` dos endereços antigos e o `CAMINHO_SEM_TAREFA`). Review rcrm3 spec (B1):
 * o funil só vale se for uuid (valor da URL saneado); `funilDoPedido` ainda confere que ele está entre os ativos.
 */
export function lerPedidoDoRelatorio(busca: string): { funil: string; estatisticas: boolean; semTarefa: boolean } {
  const q = new URLSearchParams(busca);
  return { funil: uuidDaUrl(q.get('funil')), estatisticas: q.get('estatisticas') === '1', semTarefa: q.get('sem_tarefa') === '1' };
}

/** Review rcrm3 spec (B1): o funil escolhido (ou o do endereço) só se estiver entre os ativos; senão, o padrão (ou o primeiro). */
export function funilDoPedido(escolhido: string, ativos: readonly { id: string; padrao?: boolean }[]): string {
  return ativos.find((f) => f.id === escolhido)?.id || ativos.find((f) => f.padrao)?.id || ativos[0]?.id || '';
}

/**
 * Lista de 03/11, item 6: o último funil que a pessoa escolheu no Comercial, por pessoa e workspace. Só a troca no seletor
 * grava (o funil pedido no endereço manda naquela visita, sem mudar o lembrado); arquivado ou excluído cai no padrão pelo
 * `funilDoPedido`. Sem armazenamento (janela privada), vale o padrão.
 */
const chaveDoFunil = (workspaceId: string, usuarioId: string) => `pipeline.crm.funil.${workspaceId}.${usuarioId}`;
export function lerFunilLembrado(workspaceId: string, usuarioId: string): string {
  try {
    return uuidDaUrl(localStorage.getItem(chaveDoFunil(workspaceId, usuarioId)));
  } catch {
    return '';
  }
}
export function lembrarFunil(workspaceId: string, usuarioId: string, funilId: string) {
  try {
    localStorage.setItem(chaveDoFunil(workspaceId, usuarioId), funilId);
  } catch {
    // Sem armazenamento (janela privada): a escolha vale só nesta visita.
  }
}

/**
 * Ajustes do CRM 27/10 (C19, decisão 10): a tarefa abre a ficha da negociação já na aba Tarefas. Lista B de 30/10 (item 28):
 * com o id da tarefa (`?tarefa=`) a ficha abre a edição dela (ou a destaca, se a pessoa não edita).
 */
export const caminhoDaTarefa = (oportunidadeId: string, tarefaId?: string) => `${caminhoDaNegociacao(oportunidadeId)}?aba=tarefas${tarefaId ? `&tarefa=${tarefaId}` : ''}`;

/** Lista B de 30/10 (item 28): a reunião da Agenda com negociação abre a ficha na aba Tarefas com a reunião em destaque (`?reuniao=`). */
export const caminhoDaReuniaoNaFicha = (oportunidadeId: string, agendamentoId: string) => `${caminhoDaNegociacao(oportunidadeId)}?aba=tarefas&reuniao=${agendamentoId}`;

/** Lista B de 30/10 (item 28): a reunião sem negociação abre o agendamento na Reuniões/Agenda do CRM (o drawer pelo `?agendamento=`). */
export const caminhoDoAgendamento = (agendamentoId: string) => `/crm/reunioes?agendamento=${agendamentoId}`;
